import { applyI18n, t } from '../../i18n';
import type { SedrAuditEvent } from '../../api/sedr';
import { enhanceDataTableCards } from '../../ui/dataTableCards';
import { renderSedrAuditDocumentCell } from './sedrUi';

function tSedrAuditAction(action: string): string {
  const key = `sedr.audit.action.${action}`;
  const translated = t(key);
  return translated === key ? action : translated;
}

function tSedrStatus(status: string): string {
  const key = `sedr.status.${status}`;
  const translated = t(key);
  return translated === key ? status : translated;
}

export interface SedrAuditFieldChange {
  field: string;
  oldValue: string | null;
  newValue: string | null;
}

export interface SedrAuditDetails {
  requirementCode?: string;
  status?: string;
  previousStatus?: string;
  requestedLifecycleStatus?: string;
  supersededByRequirementId?: string;
  supersededByRequirementCode?: string;
  title?: string;
  description?: string;
  category?: string;
  categoryName?: string;
  ownerName?: string;
  testMethodFrequency?: string;
  lastTestDate?: string;
  linkedFileId?: string;
  linkedFileName?: string;
  phaseId?: string;
  phaseName?: string;
  approvalValidityAmount?: number;
  approvalValidityUnit?: string;
  approvalValidityDays?: number;
  permission?: string;
  previousPermission?: string;
  canApprove?: boolean;
  previousCanApprove?: boolean;
  allowGuestLinks?: boolean;
  previousAllowGuestLinks?: boolean;
  requireGuestTotp?: boolean;
  previousRequireGuestTotp?: boolean;
  displayName?: string;
  organization?: string;
  userId?: string;
  name?: string;
  fileName?: string;
  path?: string;
  contentType?: string;
  summary?: string;
  created?: number;
  skipped?: number;
  requirementsCreated?: number;
  artifactsLinked?: number;
  purgeAt?: string;
  reason?: string;
  requestedBy?: string;
  startAt?: string;
  endAt?: string;
  sortOrder?: number;
  linkType?: string;
  linkTypeName?: string;
  fromRequirementId?: string;
  fromRequirementCode?: string;
  toRequirementId?: string;
  toRequirementCode?: string;
  inverseName?: string;
  changes?: SedrAuditFieldChange[];
  [key: string]: unknown;
}

export interface SedrAuditLookup {
  requirementsById: Map<string, { code: string; title: string }>;
  requirementsByCode: Map<string, string>;
  phasesById: Map<string, string>;
  filesById: Map<string, { name: string; parentId: string | null }>;
  membersById: Map<string, string>;
  categoriesById: Map<string, string>;
}

export function createEmptySedrAuditLookup(): SedrAuditLookup {
  return {
    requirementsById: new Map(),
    requirementsByCode: new Map(),
    phasesById: new Map(),
    filesById: new Map(),
    membersById: new Map(),
    categoriesById: new Map(),
  };
}

export function buildSedrAuditLookup(input: {
  requirements?: Array<{ id: string; requirementCode: string; title: string }>;
  phases?: Array<{ id: string; name: string }>;
  files?: Array<{ id: string; name: string; parentId?: string | null }>;
  members?: Array<{ userId: string; displayName: string }>;
  categories?: Array<{ id: string; name: string }>;
}): SedrAuditLookup {
  const lookup = createEmptySedrAuditLookup();
  for (const req of input.requirements ?? []) {
    lookup.requirementsById.set(req.id, { code: req.requirementCode, title: req.title });
    lookup.requirementsByCode.set(req.requirementCode, req.id);
  }
  for (const phase of input.phases ?? []) {
    lookup.phasesById.set(phase.id, phase.name);
  }
  for (const file of input.files ?? []) {
    lookup.filesById.set(file.id, { name: file.name, parentId: file.parentId ?? null });
  }
  for (const member of input.members ?? []) {
    lookup.membersById.set(member.userId, member.displayName);
  }
  for (const category of input.categories ?? []) {
    lookup.categoriesById.set(category.id, category.name);
  }
  return lookup;
}

const STATUS_FIELDS = new Set([
  'status',
  'previousStatus',
  'requestedLifecycleStatus',
  'newStatus',
]);

const GUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

function looksLikeGuid(value: string | null | undefined): boolean {
  return Boolean(value && GUID_RE.test(value));
}

function asString(value: unknown): string | undefined {
  return typeof value === 'string' && value ? value : undefined;
}

function asNumber(value: unknown): number | undefined {
  return typeof value === 'number' && Number.isFinite(value) ? value : undefined;
}

function asBoolean(value: unknown): boolean | undefined {
  return typeof value === 'boolean' ? value : undefined;
}

function parseChanges(raw: unknown): SedrAuditFieldChange[] | undefined {
  if (!Array.isArray(raw)) {
    return undefined;
  }

  const changes: SedrAuditFieldChange[] = [];
  for (const item of raw) {
    if (!item || typeof item !== 'object') {
      continue;
    }
    const row = item as Record<string, unknown>;
    const field = asString(row.field);
    if (!field) {
      continue;
    }
    changes.push({
      field,
      oldValue: typeof row.oldValue === 'string' ? row.oldValue : row.oldValue == null ? null : String(row.oldValue),
      newValue: typeof row.newValue === 'string' ? row.newValue : row.newValue == null ? null : String(row.newValue),
    });
  }
  return changes.length > 0 ? changes : undefined;
}

export function parseSedrAuditDetails(detailsJson: string | null): SedrAuditDetails | null {
  if (!detailsJson) {
    return null;
  }

  try {
    const raw = JSON.parse(detailsJson) as Record<string, unknown>;
    return {
      ...raw,
      requirementCode: asString(raw.requirementCode),
      status: asString(raw.status),
      previousStatus: asString(raw.previousStatus),
      requestedLifecycleStatus: asString(raw.requestedLifecycleStatus),
      supersededByRequirementId: asString(raw.supersededByRequirementId),
      supersededByRequirementCode: asString(raw.supersededByRequirementCode),
      title: asString(raw.title),
      description: asString(raw.description),
      category: asString(raw.category),
      categoryName: asString(raw.categoryName),
      ownerName: asString(raw.ownerName),
      testMethodFrequency: asString(raw.testMethodFrequency),
      lastTestDate: asString(raw.lastTestDate),
      linkedFileId: asString(raw.linkedFileId),
      linkedFileName: asString(raw.linkedFileName),
      phaseId: asString(raw.phaseId),
      phaseName: asString(raw.phaseName),
      approvalValidityAmount: asNumber(raw.approvalValidityAmount),
      approvalValidityUnit: asString(raw.approvalValidityUnit),
      approvalValidityDays: asNumber(raw.approvalValidityDays),
      permission: asString(raw.permission),
      previousPermission: asString(raw.previousPermission),
      canApprove: asBoolean(raw.canApprove),
      previousCanApprove: asBoolean(raw.previousCanApprove),
      allowGuestLinks: asBoolean(raw.allowGuestLinks),
      previousAllowGuestLinks: asBoolean(raw.previousAllowGuestLinks),
      requireGuestTotp: asBoolean(raw.requireGuestTotp),
      previousRequireGuestTotp: asBoolean(raw.previousRequireGuestTotp),
      displayName: asString(raw.displayName),
      organization: asString(raw.organization),
      userId: asString(raw.userId),
      name: asString(raw.name),
      fileName: asString(raw.fileName),
      path: asString(raw.path),
      contentType: asString(raw.contentType),
      summary: asString(raw.summary),
      created: asNumber(raw.created),
      skipped: asNumber(raw.skipped),
      requirementsCreated: asNumber(raw.requirementsCreated),
      artifactsLinked: asNumber(raw.artifactsLinked),
      purgeAt: asString(raw.purgeAt),
      reason: asString(raw.reason),
      requestedBy: asString(raw.requestedBy),
      startAt: asString(raw.startAt),
      endAt: asString(raw.endAt),
      sortOrder: asNumber(raw.sortOrder),
      linkType: asString(raw.linkType),
      linkTypeName: asString(raw.linkTypeName),
      fromRequirementId: asString(raw.fromRequirementId),
      fromRequirementCode: asString(raw.fromRequirementCode),
      toRequirementId: asString(raw.toRequirementId),
      toRequirementCode: asString(raw.toRequirementCode),
      inverseName: asString(raw.inverseName),
      changes: parseChanges(raw.changes),
    };
  } catch {
    return null;
  }
}

function formatFieldLabel(field: string): string {
  const key = `sedr.audit.detail.${field}`;
  const translated = t(key);
  return translated === key ? field : translated;
}

type ResolvedValue =
  | { kind: 'text'; text: string }
  | { kind: 'link'; text: string; onClick: () => void }
  | { kind: 'omit' }
  | { kind: 'empty' };

function resolveRequirementLabel(
  lookup: SedrAuditLookup | undefined,
  id: string | null | undefined,
  code: string | null | undefined,
): { id: string | null; code: string | null } {
  if (id && lookup?.requirementsById.has(id)) {
    return { id, code: lookup.requirementsById.get(id)!.code };
  }
  if (code && lookup?.requirementsByCode.has(code)) {
    return { id: lookup.requirementsByCode.get(code)!, code };
  }
  if (code) {
    return { id: id ?? null, code };
  }
  if (id && !looksLikeGuid(id)) {
    return { id, code: id };
  }
  return { id: id ?? null, code: code ?? null };
}

function resolveFieldValue(
  field: string,
  value: unknown,
  details: SedrAuditDetails | null,
  lookup: SedrAuditLookup | undefined,
  options: RenderSedrAuditDetailOptions,
): ResolvedValue {
  if (value == null || value === '') {
    return { kind: 'empty' };
  }

  if (typeof value === 'boolean') {
    return { kind: 'text', text: value ? t('sedr.audit.detail.yes') : t('sedr.audit.detail.no') };
  }

  if (STATUS_FIELDS.has(field) && typeof value === 'string') {
    return { kind: 'text', text: tSedrStatus(value) };
  }

  if ((field === 'lastTestDate' || field === 'startAt' || field === 'endAt' || field === 'purgeAt')
    && typeof value === 'string') {
    const date = new Date(value);
    return { kind: 'text', text: Number.isNaN(date.getTime()) ? value : date.toLocaleString() };
  }

  if (field === 'phaseId' || field === 'phase') {
    const id = typeof value === 'string' ? value : details?.phaseId;
    const storedName = details?.phaseName;
    const name = storedName
      || (id && lookup?.phasesById.get(id))
      || (!looksLikeGuid(String(value)) ? String(value) : null);
    if (!name) {
      return { kind: 'omit' };
    }
    if (id && options.onOpenPhase) {
      return { kind: 'link', text: name, onClick: () => options.onOpenPhase?.(id) };
    }
    return { kind: 'text', text: name };
  }

  if (field === 'linkedFileId' || field === 'linkedFile') {
    const id = typeof value === 'string' && looksLikeGuid(value) ? value : details?.linkedFileId;
    const storedName = details?.linkedFileName
      || details?.fileName
      || (!looksLikeGuid(String(value)) ? String(value) : null);
    const file = id ? lookup?.filesById.get(id) : undefined;
    const name = storedName || file?.name || null;
    if (!name) {
      return { kind: 'omit' };
    }
    if (id && options.onOpenFile) {
      return { kind: 'link', text: name, onClick: () => options.onOpenFile?.(id) };
    }
    return { kind: 'text', text: name };
  }

  if (field === 'category') {
    const id = typeof value === 'string' ? value : details?.category;
    const name = details?.categoryName
      || (id ? lookup?.categoriesById.get(id) : undefined)
      || (!looksLikeGuid(String(value)) ? String(value) : null);
    if (!name) {
      return { kind: 'omit' };
    }
    return { kind: 'text', text: name };
  }

  if (field === 'userId') {
    const id = String(value);
    const name = lookup?.membersById.get(id) || details?.displayName;
    if (!name) {
      return { kind: 'omit' };
    }
    return { kind: 'text', text: name };
  }

  if (field === 'supersededByRequirementId' || field === 'supersededByRequirementCode' || field === 'supersededBy') {
    const resolved = resolveRequirementLabel(
      lookup,
      details?.supersededByRequirementId ?? (field === 'supersededByRequirementId' ? String(value) : null),
      details?.supersededByRequirementCode ?? (field === 'supersededByRequirementCode' ? String(value) : null),
    );
    if (!resolved.code) {
      return { kind: 'omit' };
    }
    if (resolved.id && options.onOpenRequirement) {
      return {
        kind: 'link',
        text: resolved.code,
        onClick: () => options.onOpenRequirement?.(resolved.id!),
      };
    }
    return { kind: 'text', text: resolved.code };
  }

  if (field === 'fromRequirementCode' || field === 'fromRequirementId' || field === 'fromRequirement') {
    const resolved = resolveRequirementLabel(
      lookup,
      details?.fromRequirementId ?? (field === 'fromRequirementId' ? String(value) : null),
      details?.fromRequirementCode ?? (field === 'fromRequirementCode' ? String(value) : null),
    );
    if (!resolved.code) {
      return { kind: 'omit' };
    }
    if (resolved.id && options.onOpenRequirement) {
      return {
        kind: 'link',
        text: resolved.code,
        onClick: () => options.onOpenRequirement?.(resolved.id!),
      };
    }
    return { kind: 'text', text: resolved.code };
  }

  if (field === 'toRequirementCode' || field === 'toRequirementId' || field === 'toRequirement') {
    const resolved = resolveRequirementLabel(
      lookup,
      details?.toRequirementId ?? (field === 'toRequirementId' ? String(value) : null),
      details?.toRequirementCode ?? (field === 'toRequirementCode' ? String(value) : null),
    );
    if (!resolved.code) {
      return { kind: 'omit' };
    }
    if (resolved.id && options.onOpenRequirement) {
      return {
        kind: 'link',
        text: resolved.code,
        onClick: () => options.onOpenRequirement?.(resolved.id!),
      };
    }
    return { kind: 'text', text: resolved.code };
  }

  if (field === 'requirementCode') {
    const code = String(value);
    const id = lookup?.requirementsByCode.get(code)
      ?? (details && lookup
        ? [...lookup.requirementsById.entries()].find(([, r]) => r.code === code)?.[0]
        : undefined)
      ?? null;
    if (id && options.onOpenRequirement) {
      return { kind: 'link', text: code, onClick: () => options.onOpenRequirement?.(id) };
    }
    return { kind: 'text', text: code };
  }

  const text = String(value);
  if (looksLikeGuid(text)) {
    return { kind: 'omit' };
  }
  return { kind: 'text', text };
}

function formatFieldValueForChange(
  field: string,
  value: string | null,
  details: SedrAuditDetails | null,
  lookup: SedrAuditLookup | undefined,
  options: RenderSedrAuditDetailOptions,
): ResolvedValue {
  if (value == null || value === '') {
    return { kind: 'empty' };
  }
  return resolveFieldValue(field, value, details, lookup, options);
}

function appendResolvedNode(parent: HTMLElement, resolved: ResolvedValue): void {
  if (resolved.kind === 'omit') {
    return;
  }
  if (resolved.kind === 'empty') {
    const span = document.createElement('span');
    span.className = 'sedr-audit-null';
    span.textContent = t('sedr.audit.detail.empty');
    parent.append(span);
    return;
  }
  if (resolved.kind === 'link') {
    const btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'text-link sedr-audit-ref';
    btn.textContent = resolved.text;
    btn.addEventListener('click', (e) => {
      e.stopPropagation();
      resolved.onClick();
    });
    parent.append(btn);
    return;
  }
  parent.append(document.createTextNode(resolved.text));
}

/** Extra fragment after requirement code for the document column / activity feed. */
export function formatSedrAuditRequirementExtra(details: SedrAuditDetails): string | null {
  if (details.summary) {
    return details.summary;
  }

  const lifecycle = details.requestedLifecycleStatus;
  if (lifecycle) {
    if (lifecycle === 'Superseded' && details.supersededByRequirementCode) {
      return t('sedr.audit.summary.superseded', {
        status: tSedrStatus(lifecycle),
        code: details.supersededByRequirementCode,
      });
    }
    return tSedrStatus(lifecycle);
  }

  if (details.status && !details.changes?.length) {
    return tSedrStatus(details.status);
  }

  if (details.changes && details.changes.length > 0) {
    if (details.changes.length === 1) {
      const change = details.changes[0]!;
      return t('sedr.audit.summary.oneField', { field: formatFieldLabel(change.field) });
    }
    return t('sedr.audit.summary.fieldCount', { count: details.changes.length });
  }

  if (details.title) {
    return details.title;
  }

  return null;
}

export function formatSedrAuditSummaryExtra(detailsJson: string | null): string | null {
  const details = parseSedrAuditDetails(detailsJson);
  if (!details?.requirementCode) {
    return null;
  }
  return formatSedrAuditRequirementExtra(details);
}

export interface RenderSedrAuditDetailOptions {
  lookup?: SedrAuditLookup;
  onOpenRequirement?: (requirementId: string) => void;
  onOpenPhase?: (phaseId: string) => void;
  onOpenFile?: (fileId: string) => void;
  /** When true, omit action/time/actor header (already shown on the table row). */
  compact?: boolean;
}

export function renderSedrAuditDetailEmpty(host: HTMLElement): void {
  host.replaceChildren();
  const empty = document.createElement('div');
  empty.className = 'sedr-audit-detail sedr-audit-detail--empty';
  empty.innerHTML = `<p class="muted sedr-audit-detail__legend" data-i18n="sedr.audit.detail.selectHint"></p>`;
  applyI18n(empty);
  host.append(empty);
}

function appendKvRow(
  list: HTMLElement,
  label: string,
  resolved: ResolvedValue,
): boolean {
  if (resolved.kind === 'omit') {
    return false;
  }
  const row = document.createElement('div');
  row.className = 'sedr-audit-kv';
  const dt = document.createElement('dt');
  dt.textContent = label;
  const dd = document.createElement('dd');
  appendResolvedNode(dd, resolved);
  row.append(dt, dd);
  list.append(row);
  return true;
}

export function renderSedrAuditDetailPanel(
  host: HTMLElement,
  event: SedrAuditEvent,
  options: RenderSedrAuditDetailOptions = {},
): void {
  host.replaceChildren();
  const details = parseSedrAuditDetails(event.detailsJson);
  const lookup = options.lookup;
  const panel = document.createElement('div');
  panel.className = 'sedr-audit-detail';

  if (!options.compact) {
    const header = document.createElement('header');
    header.className = 'sedr-audit-detail__identity';
    const title = document.createElement('div');
    title.className = 'sedr-audit-detail__title';
    title.textContent = tSedrAuditAction(event.action);
    const meta = document.createElement('p');
    meta.className = 'sedr-audit-detail__meta muted';
    meta.textContent = [
      new Date(event.createdAt).toLocaleString(),
      event.actorDisplay ?? t('sedr.audit.detail.unknownActor'),
    ].join(' · ');
    header.append(title, meta);
    panel.append(header);
  }

  const subjectCode = details?.requirementCode;
  const subjectReq = resolveRequirementLabel(
    lookup,
    event.documentId,
    subjectCode,
  );
  if (subjectReq.code && !subjectReq.code.includes(' requirements')) {
    const subject = document.createElement('div');
    subject.className = 'sedr-audit-detail__subject';
    const resolved = resolveFieldValue(
      'requirementCode',
      subjectReq.code,
      details,
      lookup,
      {
        ...options,
        onOpenRequirement: options.onOpenRequirement
          ? (id) => options.onOpenRequirement?.(id)
          : undefined,
      },
    );
    // Prefer documentId for navigation when available.
    if (event.documentId && options.onOpenRequirement && subjectReq.code) {
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'text-link sedr-audit-ref sedr-audit-ref--subject';
      btn.textContent = subjectReq.code;
      btn.addEventListener('click', (e) => {
        e.stopPropagation();
        options.onOpenRequirement?.(event.documentId!);
      });
      subject.append(btn);
      if (details?.title) {
        const titleSpan = document.createElement('span');
        titleSpan.className = 'muted';
        titleSpan.textContent = ` · ${details.title}`;
        subject.append(titleSpan);
      }
    } else {
      appendResolvedNode(subject, resolved);
    }
    panel.append(subject);
  }

  if (details?.changes && details.changes.length > 0) {
    const section = document.createElement('section');
    section.className = 'sedr-audit-detail__section sedr-audit-detail__section--changes';
    const heading = document.createElement('h4');
    heading.textContent = t('sedr.audit.detail.changes');
    section.append(heading);

    const cards = document.createElement('div');
    cards.className = 'sedr-audit-change-list';
    for (const change of details.changes) {
      const card = document.createElement('article');
      card.className = 'sedr-audit-change';
      const fieldEl = document.createElement('div');
      fieldEl.className = 'sedr-audit-change__field';
      fieldEl.textContent = formatFieldLabel(change.field);
      const values = document.createElement('div');
      values.className = 'sedr-audit-change__values';

      const fromResolved = formatFieldValueForChange(
        change.field,
        change.oldValue,
        details,
        lookup,
        options,
      );
      const toResolved = formatFieldValueForChange(
        change.field,
        change.newValue,
        details,
        lookup,
        options,
      );

      if (fromResolved.kind === 'omit' && toResolved.kind === 'omit') {
        continue;
      }

      const fromSpan = document.createElement('span');
      fromSpan.className = 'sedr-audit-from';
      appendResolvedNode(fromSpan, fromResolved.kind === 'omit' ? { kind: 'empty' } : fromResolved);
      const arrow = document.createElement('span');
      arrow.className = 'sedr-audit-arrow';
      arrow.textContent = '→';
      const toSpan = document.createElement('span');
      toSpan.className = 'sedr-audit-to';
      appendResolvedNode(toSpan, toResolved.kind === 'omit' ? { kind: 'empty' } : toResolved);
      values.append(fromSpan, arrow, toSpan);
      card.append(fieldEl, values);
      cards.append(card);
    }
    if (cards.childElementCount > 0) {
      section.append(cards);
      panel.append(section);
    }
  }

  if (details) {
    const skip = new Set<string>([
      'changes',
      'summary',
      'approvalValidityDays',
      'supersededByRequirementId',
      'requirementCode',
      'title',
      // Prefer name fields over ids
      'phaseId',
      'linkedFileId',
      'userId',
      'category',
      'fromRequirementId',
      'toRequirementId',
      'linkType',
    ]);
    if (details.changes) {
      for (const change of details.changes) {
        skip.add(change.field);
      }
    }

    const detailRows: Array<{ label: string; field: string; value: unknown }> = [
      { label: t('sedr.audit.detail.status'), field: 'status', value: details.status },
      { label: t('sedr.audit.detail.previousStatus'), field: 'previousStatus', value: details.previousStatus },
      {
        label: t('sedr.audit.detail.requestedLifecycleStatus'),
        field: 'requestedLifecycleStatus',
        value: details.requestedLifecycleStatus,
      },
      {
        label: t('sedr.audit.detail.supersededBy'),
        field: 'supersededBy',
        value: details.supersededByRequirementCode ?? details.supersededByRequirementId,
      },
      {
        label: t('sedr.audit.detail.linkType'),
        field: 'linkType',
        value: details.linkTypeName ?? details.linkType,
      },
      {
        label: t('sedr.audit.detail.fromRequirement'),
        field: 'fromRequirement',
        value: details.fromRequirementCode ?? details.fromRequirementId,
      },
      {
        label: t('sedr.audit.detail.toRequirement'),
        field: 'toRequirement',
        value: details.toRequirementCode ?? details.toRequirementId,
      },
      {
        label: t('sedr.audit.detail.inverseName'),
        field: 'inverseName',
        value: details.inverseName,
      },
      {
        label: t('sedr.audit.detail.phaseId'),
        field: 'phaseId',
        value: details.phaseName ?? details.phaseId,
      },
      {
        label: t('sedr.audit.detail.linkedFileId'),
        field: 'linkedFileId',
        value: details.linkedFileName ?? details.fileName ?? details.linkedFileId,
      },
      {
        label: t('sedr.audit.detail.category'),
        field: 'category',
        value: details.categoryName ?? details.category,
      },
      { label: t('sedr.audit.detail.description'), field: 'description', value: details.description },
      { label: t('sedr.audit.detail.ownerName'), field: 'ownerName', value: details.ownerName },
      {
        label: t('sedr.audit.detail.testMethodFrequency'),
        field: 'testMethodFrequency',
        value: details.testMethodFrequency,
      },
      { label: t('sedr.audit.detail.lastTestDate'), field: 'lastTestDate', value: details.lastTestDate },
      {
        label: t('sedr.audit.detail.approvalValidityAmount'),
        field: 'approvalValidityAmount',
        value: details.approvalValidityAmount,
      },
      {
        label: t('sedr.audit.detail.approvalValidityUnit'),
        field: 'approvalValidityUnit',
        value: details.approvalValidityUnit,
      },
      { label: t('sedr.audit.detail.permission'), field: 'permission', value: details.permission },
      {
        label: t('sedr.audit.detail.previousPermission'),
        field: 'previousPermission',
        value: details.previousPermission,
      },
      { label: t('sedr.audit.detail.canApprove'), field: 'canApprove', value: details.canApprove },
      {
        label: t('sedr.audit.detail.previousCanApprove'),
        field: 'previousCanApprove',
        value: details.previousCanApprove,
      },
      {
        label: t('sedr.audit.detail.allowGuestLinks'),
        field: 'allowGuestLinks',
        value: details.allowGuestLinks,
      },
      {
        label: t('sedr.audit.detail.previousAllowGuestLinks'),
        field: 'previousAllowGuestLinks',
        value: details.previousAllowGuestLinks,
      },
      {
        label: t('sedr.audit.detail.requireGuestTotp'),
        field: 'requireGuestTotp',
        value: details.requireGuestTotp,
      },
      {
        label: t('sedr.audit.detail.previousRequireGuestTotp'),
        field: 'previousRequireGuestTotp',
        value: details.previousRequireGuestTotp,
      },
      { label: t('sedr.audit.detail.displayName'), field: 'displayName', value: details.displayName },
      { label: t('sedr.audit.detail.organization'), field: 'organization', value: details.organization },
      {
        label: t('sedr.audit.detail.userId'),
        field: 'userId',
        value: details.userId,
      },
      { label: t('sedr.audit.detail.name'), field: 'name', value: details.name },
      { label: t('sedr.audit.detail.fileName'), field: 'fileName', value: details.fileName },
      { label: t('sedr.audit.detail.path'), field: 'path', value: details.path },
      { label: t('sedr.audit.detail.startAt'), field: 'startAt', value: details.startAt },
      { label: t('sedr.audit.detail.endAt'), field: 'endAt', value: details.endAt },
      { label: t('sedr.audit.detail.sortOrder'), field: 'sortOrder', value: details.sortOrder },
      { label: t('sedr.audit.detail.purgeAt'), field: 'purgeAt', value: details.purgeAt },
      { label: t('sedr.audit.detail.deletionReason'), field: 'reason', value: details.reason },
      { label: t('sedr.audit.detail.requestedBy'), field: 'requestedBy', value: details.requestedBy },
    ];

    const list = document.createElement('dl');
    list.className = 'sedr-audit-kv-list';
    let hasRows = false;

    for (const row of detailRows) {
      if (skip.has(row.field) && row.field !== 'phaseId' && row.field !== 'linkedFileId'
        && row.field !== 'category' && row.field !== 'userId' && row.field !== 'supersededBy'
        && row.field !== 'fromRequirement' && row.field !== 'toRequirement' && row.field !== 'linkType') {
        continue;
      }
      // Skip id-backed rows when changes already covered them
      if (details.changes?.some((c) => c.field === row.field
        || (row.field === 'phaseId' && c.field === 'phaseId')
        || (row.field === 'linkedFileId' && c.field === 'linkedFileId')
        || (row.field === 'category' && c.field === 'category'))) {
        continue;
      }
      if (row.value == null || row.value === '') {
        continue;
      }
      const resolved = resolveFieldValue(row.field, row.value, details, lookup, options);
      if (appendKvRow(list, row.label, resolved)) {
        hasRows = true;
      }
    }

    if (typeof details.created === 'number') {
      if (appendKvRow(list, t('sedr.audit.detail.import'), {
        kind: 'text',
        text: t('sedr.audit.importSummary', {
          created: details.created,
          skipped: details.skipped ?? 0,
        }),
      })) {
        hasRows = true;
      }
    }

    if (hasRows) {
      const section = document.createElement('section');
      section.className = 'sedr-audit-detail__section';
      const heading = document.createElement('h4');
      heading.textContent = t('sedr.audit.detail.details');
      section.append(heading, list);
      panel.append(section);
    }
  } else if (!event.detailsJson) {
    const none = document.createElement('p');
    none.className = 'muted sedr-audit-detail__legend';
    none.textContent = t('sedr.audit.detail.noDetails');
    panel.append(none);
  }

  host.append(panel);
}

/**
 * Full-width audit table with Stitch-style expand-below-row details.
 * Click a row to toggle its detail panel under that row.
 */
export function mountSedrAuditExpandableTable(
  host: HTMLElement,
  events: SedrAuditEvent[],
  options: RenderSedrAuditDetailOptions = {},
): void {
  host.replaceChildren();
  const wrap = document.createElement('div');
  wrap.className = 'files-table-wrap sedr-audit-table-wrap';
  wrap.innerHTML = `
    <table class="data-table sedr-audit-table" role="grid">
      <thead>
        <tr>
          <th data-i18n="sedr.audit.time"></th>
          <th data-i18n="sedr.audit.action"></th>
          <th data-i18n="sedr.audit.actor"></th>
          <th data-i18n="sedr.audit.document"></th>
        </tr>
      </thead>
      <tbody></tbody>
    </table>
  `;
  applyI18n(wrap);
  const tbody = wrap.querySelector('tbody') as HTMLTableSectionElement;
  const expanded = new Set<string>();

  const setExpanded = (eventId: string, open: boolean) => {
    const row = tbody.querySelector(`tr.sedr-audit-row[data-event-id="${CSS.escape(eventId)}"]`) as HTMLTableRowElement | null;
    const detailsRow = tbody.querySelector(`tr.sedr-audit-details-row[data-event-id="${CSS.escape(eventId)}"]`) as HTMLTableRowElement | null;
    if (!row || !detailsRow) {
      return;
    }
    row.classList.toggle('sedr-audit-row--selected', open);
    row.setAttribute('aria-expanded', open ? 'true' : 'false');
    row.setAttribute('aria-selected', open ? 'true' : 'false');
    detailsRow.hidden = !open;
    if (open) {
      expanded.add(eventId);
      const detailHost = detailsRow.querySelector('.sedr-audit-detail-host') as HTMLElement;
      const evt = events.find((e) => e.id === eventId);
      if (evt && detailHost) {
        renderSedrAuditDetailPanel(detailHost, evt, { ...options, compact: true });
      }
    } else {
      expanded.delete(eventId);
      const detailHost = detailsRow.querySelector('.sedr-audit-detail-host') as HTMLElement;
      detailHost?.replaceChildren();
    }
  };

  const toggleEvent = (eventId: string) => {
    setExpanded(eventId, !expanded.has(eventId));
  };

  if (events.length === 0) {
    const empty = document.createElement('p');
    empty.className = 'muted sedr-audit-detail__legend';
    empty.setAttribute('data-i18n', 'sedr.audit.empty');
    // Fall back if key missing
    empty.textContent = t('sedr.audit.detail.selectHint');
    host.append(empty);
    applyI18n(host);
    return;
  }

  for (const evt of events) {
    const tr = document.createElement('tr');
    tr.className = 'sedr-audit-row';
    tr.dataset.eventId = evt.id;
    tr.tabIndex = 0;
    tr.setAttribute('role', 'row');
    tr.setAttribute('aria-expanded', 'false');
    tr.setAttribute('aria-selected', 'false');

    const timeTd = document.createElement('td');
    timeTd.className = 'sedr-audit-time';
    timeTd.textContent = new Date(evt.createdAt).toLocaleString();
    const actionTd = document.createElement('td');
    actionTd.className = 'sedr-audit-action';
    actionTd.textContent = tSedrAuditAction(evt.action);
    const actorTd = document.createElement('td');
    actorTd.textContent = evt.actorDisplay ?? '—';
    const docTd = document.createElement('td');
    docTd.append(renderSedrAuditDocumentCell(evt.detailsJson));
    tr.append(timeTd, actionTd, actorTd, docTd);

    const detailsTr = document.createElement('tr');
    detailsTr.className = 'sedr-audit-details-row';
    detailsTr.dataset.eventId = evt.id;
    detailsTr.hidden = true;
    const detailsTd = document.createElement('td');
    detailsTd.colSpan = 4;
    const detailHost = document.createElement('div');
    detailHost.className = 'sedr-audit-detail-host';
    detailsTd.append(detailHost);
    detailsTr.append(detailsTd);

    tr.addEventListener('click', () => toggleEvent(evt.id));
    tr.addEventListener('keydown', (e) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        toggleEvent(evt.id);
      }
    });

    tbody.append(tr, detailsTr);
  }

  host.append(wrap);
  const auditTable = wrap.querySelector('table.data-table') as HTMLTableElement | null;
  if (auditTable) {
    enhanceDataTableCards(auditTable);
  }
}
